import React, { useMemo } from 'react'
import { Card } from '~/app/components'
import { getTrainImage } from '~/app/components/order-converter/converter/TrainOrderConverter'
import { LogoNolWhite, LogoKorailWhite } from '~/app/icons'
import { isoToFormat } from '~/app/utils/useIsoToFormat'
import {
  TimePanel,
  TimePanelSubGroup,
  TicketHeader,
  TicketHeaderText,
  TicketHeaderImages,
  TicketHeaderImagesDivider,
  TicketTypes,
  TicketType,
  SeatInformation,
  SeatInformationPanel,
  SeatInformationSubText,
  SeatInformationTextGroup,
} from '~/app/components/order-list/details/TrainTicket/TrainTicketStyled'
import { Icon_arrowoneway } from '@yanolja-org/ya-com-system-icon'

/**
 * TrainTicketCard component
 * author: soojin.oh
 */

// type definitions
type Props = {
  departureAt: string
  departureStationName: string
  arrivalAt: string
  arrivalStationName: string
  trainType: string
  seatInformation: [string, string, string][]
  departureDate: string
  stationTrainClassifyCode: string
  passengerInformation: string
  createdAt: string
} & React.HTMLAttributes<HTMLDivElement>

// styled components

// TrainTicketCard component
const TrainTicketCard: React.FC<Props> = function ({
  departureAt,
  departureStationName,
  arrivalAt,
  arrivalStationName,
  trainType,
  seatInformation,
  departureDate,
  stationTrainClassifyCode,
  passengerInformation,
  createdAt,
  style,
}) {
  return (
    <Card style={{ ...style, paddingBottom: '2.4rem', flexDirection: 'column' }}>
      <TicketHeader>
        <TicketHeaderText>출발일 {departureDate}</TicketHeaderText>
        <TicketHeaderImages>
          <LogoNolWhite />
          <TicketHeaderImagesDivider />
          <LogoKorailWhite />
        </TicketHeaderImages>
      </TicketHeader>
      <article>
        <TimePanel>
          <TimePanelSubGroup>
            <div className={'location'}>{departureStationName}</div>
            <div className={'time'}>{departureAt}</div>
          </TimePanelSubGroup>
          <Icon_arrowoneway size={2} unit="rem" />
          <TimePanelSubGroup>
            <div className={'location'}>{arrivalStationName}</div>
            <div className={'time'}>{arrivalAt}</div>
          </TimePanelSubGroup>
        </TimePanel>
        <TicketTypes>
          <TicketType>
            <div className={'title'}>기차 번호</div>
            <div className={'content'}>
              <div className={'logo'}> {getTrainImage(stationTrainClassifyCode).h16}</div>
              <div className={'number'}>{trainType}</div>
            </div>
          </TicketType>
          <TicketType>
            <div className={'title'}>예약 인원</div>
            <div className={'content'}>{passengerInformation}</div>
          </TicketType>
          <TicketType>
            <div className={'title'}>좌석 등급</div>
            <div className={'content'}>일반실</div>
          </TicketType>
          <TicketType>
            <div className={'title'}>발행일</div>
            <div className={'content'}>{isoToFormat(createdAt, 'yyyy.MM.dd (ccc)')}</div>
          </TicketType>
        </TicketTypes>
        <SeatInformationPanel>
          {useMemo(
            () =>
              seatInformation.map(([trainNo, seatNumber, direction], index) => (
                <SeatInformation key={index}>
                  <SeatInformationTextGroup>
                    <SeatInformationSubText>호차번호</SeatInformationSubText>
                    <div className={'text'}>
                      <b>{trainNo}</b>호차
                    </div>
                  </SeatInformationTextGroup>
                  <SeatInformationTextGroup>
                    <SeatInformationSubText>좌석번호</SeatInformationSubText>
                    <div className={'text'}>
                      <b>{seatNumber}</b>{' '}
                    </div>
                  </SeatInformationTextGroup>
                  <SeatInformationTextGroup>
                    <SeatInformationSubText>좌석방향</SeatInformationSubText>
                    <div className={'text'}>
                      <div className={'direction'}>{direction}</div>
                    </div>
                  </SeatInformationTextGroup>
                </SeatInformation>
              )),
            [seatInformation]
          )}
        </SeatInformationPanel>
      </article>
    </Card>
  )
}

export default TrainTicketCard
